import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Breadcrumbs,
  CardComponent,
  Chip,
  PageTitleAndSubTitle,
  Button,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Loader from '../../_components/molecules/Loader';
// import { PlaybookMessages } from '../../core-utils/messages/PlaybookConstants';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useNavigate } from 'react-router-dom';
import { truncateString } from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import axios from 'axios';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import properties from '../../core-utils/properties';
import { useStores } from '../../stores';
import Snackbar from '../../_components/atoms/Snackbar';
import { PLAYBOOK, PLAYBOOK_DETAILS } from '../../core-utils/routes';

export interface PlaybookInternalPageProps {}

const PlaybookInternalPage: React.FC<PlaybookInternalPageProps> = observer(
  () => {
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '40px 0px 66px 63px',
      },
      container: {
        marginBottom: '18px',
        width: '1182px',
        position: 'relative',
        overflow: 'auto',
      },
      cardsContainer: {
        display: 'flex',
        gap: '1rem',
        overflowX: 'auto',
        paddingBottom: '1rem',
        ...scrollBarStyles,
      },
      ellipses: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        width: '250px',
        whiteSpace: 'nowrap',
        '&:hover': {
          overflow: 'visible',
          whiteSpace: 'normal',
          height: 'auto',
        },
      },
    });
    const { classes } = useStyles();
    const navigate = useNavigate();
    // const messages = PlaybookMessages;

    const contractAiqStore = useStores().contractAiqStore;

    const [loader] = useState<any>(<Loader />);
    const [loading, setLoading] = useState<boolean>(false);
    const [playbookData, setPlaybookData] = useState<any>({});
    const [snackbarState, setSnackbarState] = React.useState<{
      open: boolean;
      message: string;
      severity: MuiColor | undefined;
      duration: null | number;
    }>({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
    const handleOpenSnackBar = (
      message: string,
      severity: MuiColor,
      duration: number | null,
    ) => {
      setSnackbarState({
        open: true,
        message: message,
        severity: severity,
        duration: duration,
      });
    };
    const handleCloseSnackBar = (
      event?: React.SyntheticEvent,
      reason?: string,
    ) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackbarState({
        open: false,
        message: '',
        severity: undefined,
        duration: null,
      });
    };

    const getPlaybookData = async () => {
      setLoading(true);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.playbookBaseUrl +
            properties.riskReport,
          {
            eg_nid: contractAiqStore?.playbookData?.eg_nid,
            playbook_id: contractAiqStore?.playbookData?.playbook_id,
          },
        )
        .then((response) => {
          if (response.status === 200) {
            setPlaybookData(response.data?.data?.[0]);
          }
        })
        .catch((error) =>
          handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
        )
        .finally(() => {
          setLoading(false);
        });
    };

    const acceptPlaybook = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.playbookBaseUrl +
            properties.acceptPlaybook,
          {
            eg_nid: contractAiqStore?.playbookData?.eg_nid,
            playbook_id: contractAiqStore?.playbookData?.playbook_id,
            type_of_report: contractAiqStore?.playbookData?.type_of_playbook,
          },
        )
        .then((response) => {
          if (response.status === 200) {
            getPlaybookData();
          }
        })
        .catch((error) =>
          handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
        );
    };

    useEffect(() => {
      getPlaybookData();
    }, []);

    return (
      <>
        {loading ? (
          loader
        ) : (
          <Grid container className={classes.root}>
            <Grid className={classes.container}>
              <Grid style={{ marginBottom: '19px' }}>
                <Breadcrumbs
                  itemList={[
                    [
                      'text',
                      'Playbook',
                      {
                        color: patientAccessTheme.palette.primary[500],
                        fontWeight: 500,
                      },
                      () => {
                        navigate(PLAYBOOK);
                      },
                    ],
                    [
                      'text',
                      playbookData?.playbook_name,
                      {
                        color: patientAccessTheme.palette.text.highemphasis,
                        fontWeight: 600,
                      },
                    ],
                  ]}
                />
              </Grid>
              <PageTitleAndSubTitle
                title={playbookData?.playbook_name}
                subTitle={playbookData?.summary}
              />

              <Grid
                style={{
                  margin: '1rem 0',
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}
              >
                <Typography
                  fontSize="12px"
                  fontType="regular"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                >
                  Applicable to:
                </Typography>

                {playbookData?.service_agreement?.map(
                  (label: any, index: any) => (
                    <Chip
                      height="25px"
                      border="0.5px solid #E5E9F0"
                      backgroundColor="#FFFFFF"
                      label={
                        <Tooltip
                          disableFocusListener
                          placement="top-end"
                          title={
                            <Typography fontSize="12px">{label}</Typography>
                          }
                          arrow={true}
                          key={index}
                        >
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: 400,
                              color:
                                patientAccessTheme.palette.text.mediumemphasis,
                            }}
                          >
                            {label}
                          </div>
                        </Tooltip>
                      }
                    />
                  ),
                )}
              </Grid>

              <Grid
                style={{
                  margin: '3rem 0',
                  display: 'flex',
                  gap: '1rem',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  fontSize="14px"
                  fontType="semiBold"
                  textColor={patientAccessTheme.palette.grey[700]}
                >
                  Key Highlights
                </Typography>

                <Grid className={classes.cardsContainer}>
                  {playbookData?.standards?.map(
                    (cardContent: any, index: any) => (
                      <CardComponent
                        title={
                          <Typography
                            fontSize="16px"
                            fontType="semiBold"
                            lineHeight="24px"
                            textColor={
                              patientAccessTheme.palette.text.highemphasis
                            }
                            margin="0px -8px 0px 0px"
                            className={
                              cardContent?.standard_id.length > 45
                                ? classes.ellipses
                                : ''
                            }
                          >
                            {cardContent?.standard_id}
                          </Typography>
                        }
                        body={
                          cardContent?.standard_name?.definition.length >
                          300 ? (
                            <Tooltip
                              disableFocusListener
                              placement="top-end"
                              title={
                                <Typography fontSize="12px">
                                  {cardContent.standard_name.definition}
                                </Typography>
                              }
                              arrow={true}
                              key={index}
                              style={{
                                zIndex: 99999,
                                minWidth: '357px',
                                maxWidth: '424px',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 400,
                                  color:
                                    patientAccessTheme.palette.text
                                      .mediumemphasis,
                                }}
                              >
                                {truncateString(
                                  cardContent.standard_name.definition,
                                  300,
                                )}
                              </div>
                            </Tooltip>
                          ) : (
                            <Typography
                              fontSize="12px"
                              fontType="regular"
                              textColor={
                                patientAccessTheme.palette.text.mediumemphasis
                              }
                            >
                              {cardContent.standard_name.definition}
                            </Typography>
                          )
                        }
                        height="220px"
                        width="300px"
                        mainGridStyles={{ minWidth: '300px' }}
                      />
                    ),
                  )}
                </Grid>
              </Grid>
              <Grid
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  height="36px"
                  width="190px"
                  label="View Detailed Playbook"
                  onClick={() => navigate(PLAYBOOK_DETAILS)}
                />
                {!playbookData?.flag && (
                  <Button
                    variant="contained"
                    color="primary"
                    height="36px"
                    width="190px"
                    label="Accept & Continue"
                    onClick={() => acceptPlaybook()}
                  />
                )}
              </Grid>
            </Grid>
            <Snackbar
              {...snackbarState}
              handleClose={handleCloseSnackBar}
              vertical="bottom"
              horizontal="right"
            />
          </Grid>
        )}
      </>
    );
  },
);

export default PlaybookInternalPage;
